import { http } from '@article/pixel';
import LocationService from '@/services/LocationService';
import Tracking from '../components/commons/Tracking.js';

function extractStringComponentSafe(string, split, index = 0) {
  if(!string || !split) {
    return '';
  }
  const components = string.split(split);

  if(index < components.length) {
    return components[index];
  }

  return '';
}

export default class ProductLegacyService {

  static upgradeLegacyState(legacyState) {
    if(!legacyState) {
      return {};
    }
    const legacyProduct = legacyState.product || {};
    const legacySpecs = legacyProduct.specs || {};
    const legacyConfigs = legacyState.configs || {};
    const legacyMedias = legacyState.medias || [];
    const legacyCollection = legacyState.collection || {};
    const legacyPrice = legacyState.price || {};
    const legacyCategory = legacyState.category || {};
    const legacyFeatures = legacyProduct.features || {};
    const legacyRating = legacyState.rating || {};
    const legacyRelated = legacyState.related || {};
    const legacyRelatedPrices = legacyState.relatedPrices || {};
    const legacyVariations = legacyState.variations || [];
    const common = legacyState.common || {};
    const categories = common.categories || [];

    const price = {
      currencyCode: legacyPrice.currencyCode,
      sale: legacyProduct.isOnSale ? legacyPrice.priceCents : null,
      price: legacyPrice.originalPriceNoCurrency ? Number(legacyPrice.originalPriceNoCurrency) * 100 : legacyPrice.priceCents,
      retail: Number(extractStringComponentSafe(legacyPrice.retail, '$', 1) || 0) * 100,
      clearance: legacyProduct.isOnClearance ? legacyPrice.priceCents : null
    };

    const PAIR = 2;
    if(legacyPrice.pair && legacyPrice.packagedInPair) {
      price.price = (price.price / PAIR) || null;
      price.sale = (price.sale / PAIR) || null;
      price.retail = (price.retail / PAIR) || null;
      price.clearance = (price.clearance / PAIR) || null;
    }

    const navigationCategory = categories.find(c => `${c.id}` === `${legacyCategory.id}`) || {};
    const parentNavigationCategory = navigationCategory && navigationCategory.parentId ? categories.find(c => `${c.id}` === `${navigationCategory.parentId}`) : (navigationCategory || {});

    return {
      product: {
        hashKey: `${legacyProduct.id}`,
        skuNo: legacyProduct.skuNo,
        title: legacyProduct.title,
        slug: extractStringComponentSafe(legacyProduct.productUrl, '/', 5),
        status: legacyProduct.status,
        imageUrl: legacyProduct.browseImage ? extractStringComponentSafe(legacyProduct.browseImage.url, '?', 0) : '',
        description: legacyProduct.description,
        // marketingTitle: legacyProduct.title, // no mapping??
        marketingDescription: legacyProduct.marketingDescription,
        metaTitle: `${legacyProduct.title}${navigationCategory.name ? ` - ${navigationCategory.name}` : ``}`,
        // metaDescription: legacyProduct.description, // no mapping??
        fabricAndStain: legacyProduct.fabricAndStainNames,
        packagedItemQuantity: legacyProduct.packagedItemQuantity,
        inStock: legacyPrice.instock,
        onSale: legacyProduct.isOnSale,
        onClearance: legacyProduct.isOnClearance,
        configuratorEnabled: legacyConfigs.enableConfigAttributes,
        configurationGroup: legacyConfigs.attributes ? legacyConfigs.attributes.map(config => ({
          attributeId: `${config.id}`,
          name: config.userFriendlyName || config.name,
          type: config.displayType,
          configuration: config.values ? config.values.map(value => ({
            hashKey: null, // We have to use the old method of switching product configuration for the legacy data format
            valueId: `${value.id}`,
            value: value.userFriendlyName || value.name,
            imageUrl: value.imageInfo ? extractStringComponentSafe(value.imageInfo.url, '?', 0) : '',
            selected: value.isSelected
          })) : []
        })) : [],
        spec: legacySpecs && legacySpecs.keys ? legacySpecs.keys.map((key, index) => ({
          // id: legacySpecs.specIds[index], // unused
          key,
          value: legacySpecs.values[index]
        })) : [],
        feature: legacyFeatures.features || [],
        care: legacyState.cares || [],
        media: legacyMedias.map(legacyMedia => {
          const media = Object.assign({}, (legacyMedia.thumbnail || {}), (legacyMedia.media || {}));
          const mediaFile = media.imageInfo || media.videoFile || {};
          return {
            type: media.type,
            url: extractStringComponentSafe(mediaFile.source || mediaFile.url, '?', 0),
            thumbnailUrl: extractStringComponentSafe(mediaFile.thumbnail || mediaFile.url, '?', 0),
            dashUrl: mediaFile.dash || '',
            hlsUrl: mediaFile.hls || ''
          };
        }),
        collection: {
          id: extractStringComponentSafe(legacyCollection.url, '/', 2),
          name: legacyCollection.title,
          slug: extractStringComponentSafe(legacyCollection.url, '/', 3),
          productCount: legacyCollection.productCount,
          productThumbnail: (legacyCollection.otherPopularProducts || []).map(product => ({
            hashKey: `${product.id}`,
            skuNo: product.skuNo,
            title: product.title,
            slug: extractStringComponentSafe(product.productUrl, '/', 5),
            imageUrl: extractStringComponentSafe(product.browseImage.url, '?', 0),
            onSale: product.isOnSale,
            onClearance: product.isOnClearance
          }))
        },
        navigationCategory: [{
          id: `${legacyCategory.id}`,
          name: legacyCategory.name,
          displayName: legacyCategory.displayName || legacyCategory.name,
          slug: extractStringComponentSafe(legacyCategory.url, '/', 3),
          pixleeAlbumId: null,
          parentId: parentNavigationCategory.id,
          parentName: parentNavigationCategory.name,
          parentDisplayName: parentNavigationCategory.displayName || parentNavigationCategory.name,
          parentSlug: extractStringComponentSafe(parentNavigationCategory.url, '/', 3)
        }],
        price: [price],
        review: {
          count: legacyRating.ratingCount,
          rating: legacyRating.rating
        },
        box: legacyState.boxes || [],
        taxonomyCategory: legacyProduct.taxonomyCategory || {},
        taxonomyAttribute: legacyConfigs.attributes ? legacyConfigs.attributes.map(attribute => ({
          attributeId: attribute.id,
          name: attribute.userFriendlyName,
          valueId: (attribute.values.find(v => v.isSelected) || {}).id,
        })) : [],
        related: (legacyRelated.products || []).map(related => {
          const price = (legacyRelatedPrices[related.id] || {}).price;
          const currencyCode = extractStringComponentSafe(price, '$', 0) === 'C' ? 'CAD' : 'USD';
          const priceComponent = extractStringComponentSafe(price, '$', 1);
          const priceCents = priceComponent ? Number(priceComponent) * 100 : null;
          return {
            hashKey: related.id,
            title: related.title,
            slug: extractStringComponentSafe(related.productUrl, '/', 5),
            imageUrl: related.browseImage ? extractStringComponentSafe(related.browseImage.url, '?', 0) : '',
            onSale: related.isOnSale,
            onClearance: related.isOnClearance,
            price: [{
              currencyCode,
              price: priceCents
            }]
          };
        }),
        variation: legacyVariations.map(variation => ({
          hashKey: `${variation.id}`,
          fabricAndStain: (variation.fabricAndStainNames || '').trim(),
          variationImageUrl: variation.variationImage ? extractStringComponentSafe(variation.variationImage.url, '?', 0) : '',
          title: variation.title,
          slug: extractStringComponentSafe(variation.productUrl, '/', 5)
        })),
        height: null,
        width: null,
        depth: null
      }
    };
  }

  static async loadConfigurations({ product, configurationChanged, preview = false }) {
    if(!product) {
      throw new Error('Failed to load configurations for null product');
    }

    if(!configurationChanged) {
      throw new Error('Legacy configurator must be provided with the configuration that was changed');
    }

    let queryStrings = [];

    queryStrings.push(`productId=${product.hashKey}`);
    queryStrings.push(`collectionId=${product.collection.id}`);
    queryStrings.push(`taxonomyCategoryId=${product.taxonomyCategory.id}`);

    const configurations = product.configurationGroup.map(cg => cg.configuration).flat();
    let selectedConfigurations = configurations.filter(c => c.selected).map(c => c.valueId);

    // Only send selected configurations up to the one that has been changed
    const changedIndex = selectedConfigurations.findIndex(selected => selected === configurationChanged.valueId);
    selectedConfigurations = selectedConfigurations.slice(0, changedIndex + 1);

    selectedConfigurations.forEach(selected => {
      queryStrings.push(`selectedAttributeValues=${selected}`);
    });

    if(preview) {
      queryStrings.push(`preview=${preview}`);
    }

    queryStrings = queryStrings.map(query => encodeURI(query));

    const result = await http.get(`/api/products/config/page?${queryStrings.join('&')}`);

    if(!result || !Object.keys(result).length || !result.product) {
      throw new Error(`Failed to load product configuration for product ${product.hashKey} with selected configurations ${selectedConfigurations}`);
    }

    const bottomResult = await http.get(`/api/products/${result.product.id}/data`, {
      type: 'bottom',
      productId: result.product.id,
      preview,
      page: 1
    });

    if(result && result.productConfig && result.productConfig.error) {
      console.warn('Configuration warning', result.productConfig.error);
    }

    return ProductLegacyService.upgradeLegacyState(Object.assign(result, bottomResult));
  }

  static async searchProducts(filter) {
    let result = [];
    try {
      const defaultFilter = {
        'filter.currency': LocationService.getCurrencyId(),
        'filter.inStock': false,
        'filter.onSale': false,
        'filter.onClearance': false,
      };

      const response = await http.get('/api/products', Object.assign(defaultFilter, filter));

      if (response && response.products) {
        result = [...response.products];
      }
    } catch (error) {
      Tracking.sentryCaptureError(error);
    }
    return result;
  }

  static async loadProduct(id) {
    const result = await http.get(`/api/products/${id}/data`);
    return ProductLegacyService.upgradeLegacyState(result);
  }
}
